import { useState, useLayoutEffect, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Fade from 'react-reveal/Fade';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { UpdateAppSubHeaderTitle } from '../../redux/actions/UiActions';
import MyDocuments from '../MyDesk/subViews/myDocuments/MyDocuments';
import MyInstitutionalDocuments from '../MyDesk/subViews/myInstitutionalDocuments/MyInstitutionalDocuments';
import LocalStorageService from '../../services/LocalStorageService';

import {
    WelcomeHeader,
    WelcomeSubtitle,
    PillMenuContainer,
    PillButton,
    MainDataBox
} from '../MyDesk/styles/MyDeskStyles';

function MyDocumentsPage() {
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState(1); // 0: Personales, 1: Recibidos (Por defecto Recibidos)
    const [searchQuery, setSearchQuery] = useState('');
    const [currentLang, setCurrentLang] = useState(LocalStorageService.getItem('language') || 'es');

    useEffect(() => {
        const handleLangChange = (e) => {
            if (e?.detail) {
                setCurrentLang(e.detail);
            } else {
                setCurrentLang(LocalStorageService.getItem('language') || 'es');
            }
        };
        window.addEventListener('languageChange', handleLangChange);
        return () => window.removeEventListener('languageChange', handleLangChange);
    }, []);

    const isEn = currentLang === 'en';

    useLayoutEffect(() => {
        dispatch(UpdateAppSubHeaderTitle(isEn ? 'Documents' : 'Documentos'));
    }, [dispatch, isEn]);

    return (
        <Fade right>
            {/* ── Page Header ── */}
            <WelcomeHeader>{isEn ? "My Documents" : "Mis Documentos"}</WelcomeHeader>
            <WelcomeSubtitle>
                {isEn 
                  ? "Quickly access and manage all files uploaded to your procedure requests."
                  : "Acceda y gestione todos los archivos cargados en sus solicitudes de trámites de forma rápida."}
            </WelcomeSubtitle>

            {/* ── Category Tabs ── */}
            <PillMenuContainer style={{ alignSelf: 'flex-start', marginBottom: '28px' }}>
                <PillButton active={activeTab === 0} onClick={() => setActiveTab(0)}>
                    {isEn ? "Personal" : "Personales"}
                </PillButton>
                <PillButton active={activeTab === 1} onClick={() => setActiveTab(1)}>
                    {isEn ? "Received" : "Recibidos"}
                </PillButton>
            </PillMenuContainer>

            {/* ── Main Documents Box ── */}
            <MainDataBox style={{ padding: '28px 32px' }}>

                {/* ── Inner Header: category info (left) + name search (right) ── */}
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: '24px',
                    marginBottom: '24px',
                    paddingBottom: '20px',
                    borderBottom: '1px solid #E2E8F0',
                    flexWrap: 'wrap'
                }}>
                    {/* Category description */}
                    <div style={{ flex: 1, minWidth: '220px' }}>
                        <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#192873', margin: '0 0 5px 0' }}>
                            {activeTab === 0 
                              ? (isEn ? 'Personal Documents' : 'Documentos Personales') 
                              : (isEn ? 'Received Documents' : 'Documentos Recibidos')}
                        </h3>
                        <p style={{ fontSize: '13.5px', color: '#64748B', margin: 0, lineHeight: '20px' }}>
                            {activeTab === 0
                                ? (isEn 
                                    ? 'Identity documents, powers of attorney, and personal files uploaded under citizen status.' 
                                    : 'Documentos de identidad, poderes y archivos personales cargados a título de ciudadano.')
                                : (isEn 
                                    ? 'Documents issued by the Ministry of Tourism and officially attached to your record.' 
                                    : 'Documentos emitidos por el Ministerio de Turismo y cargados a su expediente de forma oficial.')}
                        </p>
                    </div>

                    {/* Name search */}
                    <div style={{ minWidth: '260px', maxWidth: '380px', flexShrink: 0, width: '100%' }}>
                        <p style={{ fontSize: '12px', fontWeight: '700', color: '#46464F', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px 0' }}>
                            {isEn ? 'SEARCH BY NAME' : 'BUSCAR POR NOMBRE'}
                        </p>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <SearchIcon style={{ position: 'absolute', left: '12px', fontSize: '18px', color: '#94A3B8', pointerEvents: 'none' }} />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder={isEn ? "E.g. Resolution, Certification..." : "Ej. Resolución, Certificación..."}
                                style={{
                                    width: '100%',
                                    height: '42px',
                                    borderRadius: '8px',
                                    border: '1px solid #CFDCE0',
                                    padding: '0 36px 0 38px',
                                    fontSize: '13.5px',
                                    fontFamily: 'Roboto, sans-serif',
                                    color: '#0F2350',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    title={isEn ? "Clear search" : "Limpiar búsqueda"}
                                    style={{
                                        position: 'absolute',
                                        right: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: '#94A3B8',
                                        padding: '4px'
                                    }}
                                >
                                    <CloseIcon style={{ fontSize: '16px' }} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Document List ── */}
                {activeTab === 0 ? <MyDocuments searchQuery={searchQuery} /> : <MyInstitutionalDocuments searchQuery={searchQuery} />}
            </MainDataBox>
        </Fade>
    );
}

export default MyDocumentsPage;
